/**
 * <review-write>: the « Écrire un avis » link of the reviews section (sections/product-reviews.liquid) opens the
 * review app's own form instead of only scrolling down to the app block. The app's header, with its own button, is
 * hidden by assets/reviews-app.css; its toggle still opens the form, which the CSS shows as a modal. While it is
 * open, `body.rw-open` dims the page; Escape and a click outside close it (through the app's toggle, which keeps
 * the app's own state right). Without JS, or while the app is not ready, the link keeps scrolling to the app block.
 */

/** Judge.me's « write a review » toggle, and the form it shows. */
const TRIGGER = '.jdgm-write-rev-link';
const FORM = '.jdgm-form-wrapper';

/** The note Judge.me puts before its form once a review is submitted. */
const NOTE = '.jdgm-notification';

/** Length of the leaving animation (reviews-app.css). */
const CLOSE_MS = 280;

/**
 * Judge.me scrolls what it opens to the very top of the window, under the sticky header. Its `scrollTo` setting
 * returning false hands the scrolling over: the browser's, which keeps the target's scroll-margin (reviews-app.css).
 */
export function scrollBelowHeader() {
  const settings = window.jdgmSettings;
  if (!settings) return;
  settings.scrollTo = (target) => {
    target[0]?.scrollIntoView({ block: 'start' });
    return false;
  };
}

/**
 * Judge.me slides its form open and shut (jQuery's slideDown/slideUp, height animated inline): on a modal that
 * fights the theme's own animations, and the closing one ends with the form gone before ours can play. This makes
 * the app's opening and closing instant (the animation jumps to its end), everything else they do unchanged.
 *
 * @param {JudgeMe | undefined} app - `window.jdgm`.
 */
export function instantForm(app = window.jdgm) {
  if (!app || app.gsInstant) return;
  for (const name of /** @type {const} */ (['openForm', 'closeForm'])) {
    const original = app[name];
    if (typeof original !== 'function') continue;
    app[name] = function (widget, ...rest) {
      const result = original.call(this, widget, ...rest);
      widget?.find?.(FORM).stop(true, true);
      return result;
    };
  }
  app.gsInstant = true;
}

/**
 * Opens the review form of the app inside `app` and returns it, or null when there is no form (yet).
 * An open form stays open: the app's toggle would close it.
 *
 * @param {Element | null} app
 * @returns {HTMLElement | null}
 */
export function openReviewForm(app) {
  const trigger = app?.querySelector(TRIGGER);
  const form = app?.querySelector(FORM);
  if (!(trigger instanceof HTMLElement) || !(form instanceof HTMLElement)) return null;
  if (getComputedStyle(form).display === 'none') trigger.click();
  return form;
}

/** @param {Element | null} app */
export const formIsOpen = (app) => {
  const form = app?.querySelector(FORM);
  return form instanceof HTMLElement && getComputedStyle(form).display !== 'none';
};

/**
 * Closes the app's form, if open, through its toggle.
 *
 * @param {Element | null} app
 */
export function closeReviewForm(app) {
  const trigger = app?.querySelector(TRIGGER);
  if (trigger instanceof HTMLElement && formIsOpen(app)) trigger.click();
}

/**
 * Keeps `body.rw-open` in step with the app's form (opened by our link, closed by its Cancel button or after a
 * submission), and closes it on Escape or on a click outside of it. After a submission, the app's « review
 * submitted » note comes up in the modal in turn (`.rw-note`), with a button to dismiss it. Returns what stops the
 * watching.
 *
 * @param {Element} app
 * @param {() => void} [onClose] - Called once the form, and the note after it, have closed.
 * @param {string} [closeLabel] - Label of the note's button.
 */
export function watchReviewForm(app, onClose, closeLabel = '') {
  let open = false;
  let closing = 0;
  /** @type {HTMLElement | null} */
  let note = null;
  const { classList } = document.body;
  const still = () => matchMedia('(prefers-reduced-motion: reduce)').matches;
  const finish = () => {
    if (open || closing || note) return;
    classList.remove('rw-open');
    onClose?.();
  };
  const done = () => {
    closing = 0;
    classList.remove('rw-closing');
    finish();
  };
  const dismissNote = () => {
    const leaving = note;
    if (!leaving || leaving.classList.contains('rw-note-out')) return;
    const gone = () => {
      leaving.remove();
      if (note === leaving) note = null;
      finish();
    };
    if (still()) {
      gone();
      return;
    }
    leaving.classList.add('rw-note-out');
    window.setTimeout(gone, CLOSE_MS);
  };
  const showNote = () => {
    const fresh = app.querySelector(`${NOTE}:not(.rw-note)`);
    if (!(fresh instanceof HTMLElement)) return;
    note = fresh;
    fresh.classList.add('rw-note');
    fresh.setAttribute('role', 'status');
    if (closeLabel) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'rw-ok';
      button.textContent = closeLabel;
      button.addEventListener('click', dismissNote);
      (fresh.firstElementChild ?? fresh).append(button);
      button.focus({ preventScroll: true });
    }
    classList.add('rw-open');
  };
  const sync = () => {
    showNote();
    if (closing) return;
    const now = formIsOpen(app);
    if (now === open) return;
    open = now;
    if (now) {
      classList.add('rw-open');
      // Opened by the app itself: its sliding may be under way.
      instantForm();
      const form = app.querySelector(FORM);
      if (form) window.jdgm?.$?.(form).stop(true, true);
      return;
    }
    // The app hides its form at once: keep it on screen while it leaves (reviews-app.css, `rw-closing`).
    const form = app.querySelector(FORM);
    if (!(form instanceof HTMLElement) || still()) {
      done();
      return;
    }
    form.style.display = 'block';
    classList.add('rw-closing');
    closing = window.setTimeout(() => {
      form.style.display = 'none';
      done();
    }, CLOSE_MS);
  };
  const observer = new MutationObserver(sync);
  observer.observe(app, { subtree: true, childList: true, attributes: true, attributeFilter: ['style', 'class'] });
  /** @param {KeyboardEvent} event */
  const onKey = (event) => {
    if (event.key !== 'Escape') return;
    if (note) dismissNote();
    else if (open) closeReviewForm(app);
  };
  /** @param {MouseEvent} event */
  const onDocumentClick = (event) => {
    if (!(event.target instanceof Node)) return;
    if (note) {
      if (!note.contains(event.target)) dismissNote();
      return;
    }
    const form = app.querySelector(FORM);
    if (open && form && !form.contains(event.target)) closeReviewForm(app);
  };
  document.addEventListener('keydown', onKey);
  document.addEventListener('click', onDocumentClick);
  sync();
  return () => {
    clearTimeout(closing);
    observer.disconnect();
    document.removeEventListener('keydown', onKey);
    document.removeEventListener('click', onDocumentClick);
    classList.remove('rw-open', 'rw-closing');
  };
}

export class ReviewWrite extends HTMLElement {
  /** @type {(() => void) | null} */
  #stopWatching = null;

  connectedCallback() {
    this.addEventListener('click', this.onClick);
    // Watched from the start: the app also opens its form by itself (a « write a review » link of its e-mails).
    // Once the children are there.
    queueMicrotask(() => this.isConnected && this.watch(this.querySelector('a[href^="#"]')));
  }

  /**
   * Starts watching the form of the app `link` leads to, once.
   *
   * @param {Element | null} link
   */
  watch(link) {
    const app = this.appOf(link);
    if (this.#stopWatching || !app || !(link instanceof HTMLElement)) return;
    this.#stopWatching = watchReviewForm(app, () => link.focus({ preventScroll: true }), this.dataset.close);
  }

  /**
   * The app block a link leads to.
   *
   * @param {Element | null} link
   */
  appOf(link) {
    const id = decodeURIComponent(link?.getAttribute('href')?.slice(1) ?? '');
    return id ? document.getElementById(id) : null;
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    this.#stopWatching?.();
    this.#stopWatching = null;
  }

  /** @param {MouseEvent} event */
  onClick = (event) => {
    const link = event.target instanceof Element ? event.target.closest('a[href^="#"]') : null;
    if (!link) return;
    const app = this.appOf(link);
    scrollBelowHeader();
    instantForm();
    const form = openReviewForm(app);
    if (!form) return;
    event.preventDefault();
    // The click that opens the form must not count as one outside of it.
    event.stopPropagation();
    this.watch(link);
    if (form.contains(document.activeElement)) return;
    const field = /** @type {HTMLElement | null} */ (
      form.querySelector('input:not([type="hidden"], [type="file"]), textarea')
    );
    field?.focus({ preventScroll: true });
  };
}

if (!customElements.get('review-write')) customElements.define('review-write', ReviewWrite);
